import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Building2, Search, Filter, Plus, Eye, Edit, Trash2, Check, X,
  Loader2, RefreshCw, Copy, Settings, Mail, ChevronDown, ChevronUp,
  Users, FileText, DollarSign, AlertCircle, Link as LinkIcon, ToggleLeft, ToggleRight,
  Send, FileCheck, FileX, ScrollText, CheckCircle, XCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MasterPartnerManager = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [masterPartners, setMasterPartners] = useState([]);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Agreement management
  const [showAgreementPanel, setShowAgreementPanel] = useState(false);
  const [agreement, setAgreement] = useState({ title: '', content: '', version: '' });
  const [savingAgreement, setSavingAgreement] = useState(false);
  
  // Modal states
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showDocRejectModal, setShowDocRejectModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [partnerToDelete, setPartnerToDelete] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [docRejectReason, setDocRejectReason] = useState('');
  
  // Form states
  const [rejectReason, setRejectReason] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [approvalSettings, setApprovalSettings] = useState({
    commission_percentage: 10,
    can_create_leads: false
  });
  
  const [actionLoading, setActionLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };
  // Agreement variables
  const [agreementVariables, setAgreementVariables] = useState([]);
  const [showVariables, setShowVariables] = useState(false);

  useEffect(() => {
    fetchData();
    fetchAgreement();
    fetchAgreementVariables();
  }, [statusFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      
      const [partnersRes, statsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/master-partners?${params}`, { headers: authHeaders }),
        axios.get(`${API}/master-partners/stats`, { headers: authHeaders }),
        axios.get(`${API}/master-partners/settings`, { headers: authHeaders })
      ]);
      
      setMasterPartners(partnersRes.data);
      setStats(statsRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgreement = async () => {
    try {
      const response = await axios.get(`${API}/master-partners/agreement/config`, { headers: authHeaders });
      setAgreement(response.data);
    } catch (error) {
      console.error('Error fetching agreement:', error);
    }
  };

  const fetchAgreementVariables = async () => {
    try {
      const response = await axios.get(`${API}/master-partners/agreement/variables`, { headers: authHeaders });
      setAgreementVariables(response.data.variables || []);
    } catch (error) {
      console.error('Error fetching variables:', error);
    }
  };

  const handleSaveAgreement = async () => {
    setSavingAgreement(true);
    try {
      await axios.post(`${API}/master-partners/agreement/config`, agreement, { headers: authHeaders });
      alert('Agreement saved successfully');
      await fetchAgreement();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save agreement');
    } finally {
      setSavingAgreement(false);
    }
  };

  const handleApproveDocument = async (partnerId, documentId) => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/master-partners/${partnerId}/documents/${documentId}/approve`, {}, { headers: authHeaders });
      alert('Document approved');
      await fetchData();
      // Refresh selected partner details
      if (selectedPartner) {
        const response = await axios.get(`${API}/master-partners/${selectedPartner.id}`, { headers: authHeaders });
        setSelectedPartner(response.data);
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to approve document');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectDocument = async () => {
    if (!selectedPartner || !selectedDocument || !docRejectReason.trim()) return;
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/master-partners/${selectedPartner.id}/documents/${selectedDocument.id}/reject?reason=${encodeURIComponent(docRejectReason)}`,
        {},
        { headers: authHeaders }
      );
      alert('Document rejected');
      setShowDocRejectModal(false);
      setDocRejectReason('');
      setSelectedDocument(null);
      await fetchData();
      // Refresh selected partner
      const response = await axios.get(`${API}/master-partners/${selectedPartner.id}`, { headers: authHeaders });
      setSelectedPartner(response.data);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to reject document');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendAgreement = async (partnerId) => {
    setActionLoading(true);
    try {
      const response = await axios.post(`${API}/master-partners/${partnerId}/send-agreement`, {}, { headers: authHeaders });
      alert('Agreement sent successfully. Link: ' + response.data.agreement_link);
      await fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to send agreement');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedPartner) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/master-partners/${selectedPartner.id}/approve`, approvalSettings, {
        headers: authHeaders
      });
      await fetchData();
      setShowApproveModal(false);
      setSelectedPartner(null);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedPartner || !rejectReason.trim()) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/master-partners/${selectedPartner.id}/reject?reason=${encodeURIComponent(rejectReason)}`, {}, {
        headers: authHeaders
      });
      await fetchData();
      setShowRejectModal(false);
      setSelectedPartner(null);
      setRejectReason('');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedPartner || newPassword.length < 6) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/master-partners/${selectedPartner.id}/reset-password`, 
        { new_password: newPassword },
        { headers: authHeaders }
      );
      alert('Password reset successfully');
      setShowResetPasswordModal(false);
      setNewPassword('');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleLeadCreation = async (partner) => {
    try {
      await axios.put(`${API}/master-partners/${partner.id}`, {
        can_create_leads: !partner.can_create_leads
      }, { headers: authHeaders });
      await fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update');
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/master-partners/settings`, settings, { headers: authHeaders });
      alert('Settings saved successfully');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const openDeleteModal = (partner) => {
    setPartnerToDelete(partner);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!partnerToDelete) return;
    
    setActionLoading(true);
    try {
      await axios.delete(`${API}/master-partners/${partnerToDelete.id}`, { headers: authHeaders });
      setShowDeleteModal(false);
      setPartnerToDelete(null);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete master partner');
    } finally {
      setActionLoading(false);
    }
  };

  const copyReferralLink = (referralCode) => {
    const link = `${window.location.origin}/partner/signup/${referralCode}`;
    navigator.clipboard.writeText(link);
    setCopied(referralCode);
    setTimeout(() => setCopied(false), 2000);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-amber-100 text-amber-700';
      case 'under_review': return 'bg-blue-100 text-blue-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      case 'suspended': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const filteredPartners = masterPartners.filter(mp => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      mp.company_name?.toLowerCase().includes(query) ||
      mp.contact_person?.toLowerCase().includes(query) ||
      mp.email?.toLowerCase().includes(query) ||
      mp.phone?.includes(query)
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Master Partner Management</h1>
          <p className="text-gray-500 mt-1">Manage master partners who recruit and oversee partners</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowAgreementPanel(!showAgreementPanel)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
          >
            <ScrollText className="w-5 h-5" />
            Agreement
            {showAgreementPanel ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <button
            onClick={() => setShowSettingsPanel(!showSettingsPanel)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Settings className="w-5 h-5" />
            Settings
            {showSettingsPanel ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Agreement Panel */}
      {showAgreementPanel && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Master Partner Agreement</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Agreement Title</label>
                <input
                  type="text"
                  value={agreement.title || ''}
                  onChange={(e) => setAgreement({ ...agreement, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="Master Partner Agreement"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Version</label>
                <input
                  type="text"
                  value={agreement.version || ''}
                  onChange={(e) => setAgreement({ ...agreement, version: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="1.0"
                />
              </div>
            </div>
            
            {/* Variables Helper */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-purple-800">Template Variables</h4>
                <button
                  onClick={() => setShowVariables(!showVariables)}
                  className="text-sm text-purple-600 hover:text-purple-700"
                >
                  {showVariables ? 'Hide Variables' : 'Show Available Variables'}
                </button>
              </div>
              <p className="text-sm text-purple-700 mb-2">
                Use these variables in your agreement content. They will be replaced with actual values in the final PDF.
              </p>
              {showVariables && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                  {agreementVariables.map((v) => (
                    <div key={v.key} className="bg-white rounded p-2 text-sm">
                      <code className="text-purple-600 font-mono bg-purple-100 px-1 rounded">{v.key}</code>
                      <p className="text-gray-600 text-xs mt-1">{v.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Agreement Content (HTML supported)</label>
              <textarea
                value={agreement.content || ''}
                onChange={(e) => setAgreement({ ...agreement, content: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 font-mono text-sm"
                rows={12}
                placeholder={`Enter the agreement content with variables...

Example:
<p>This Master Partner Agreement is between Visaasure and <strong>{{company_name}}</strong>.</p>
<p>Contact: {{contact_person}} ({{email}})</p>
<p>Commission Rate: {{commission_percentage}}%</p>
<p>Effective Date: {{acceptance_date}}</p>`}
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleSaveAgreement}
                disabled={savingAgreement}
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {savingAgreement ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Save Agreement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Panel */}
      {showSettingsPanel && settings && (
        <div className="bg-white rounded-xl shadow-sm p-6" data-testid="settings-panel">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Master Partner Settings</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document Notification Email
              </label>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={settings.document_notification_email || ''}
                  onChange={(e) => setSettings({ ...settings, document_notification_email: e.target.value })}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="info@visaasure.com"
                  data-testid="notification-email-input"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Receives all partner & master partner document submissions
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default Commission %
              </label>
              <input
                type="number"
                value={settings.default_commission_percentage || 10}
                onChange={(e) => setSettings({ ...settings, default_commission_percentage: parseFloat(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                min="0"
                max="100"
              />
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="defaultCanCreateLeads"
                checked={settings.default_can_create_leads || false}
                onChange={(e) => setSettings({ ...settings, default_can_create_leads: e.target.checked })}
                className="w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <label htmlFor="defaultCanCreateLeads" className="text-sm text-gray-700">
                Allow lead creation by default for new master partners
              </label>
            </div>
          </div>
          <div className="mt-6 flex justify-end">
            <button
              onClick={handleSaveSettings}
              disabled={savingSettings}
              className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
              data-testid="save-settings-btn"
            >
              {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Save Settings
            </button>
          </div>
        </div>
      )}

      {/* Public Signup Link */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl p-6 text-white">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold">Master Partner Signup Link</h3>
            <p className="text-purple-100 text-sm mt-1">Share this link for new master partner registrations</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="bg-white/10 rounded-lg px-4 py-2 font-mono text-sm">
              {window.location.origin}/master-partner/signup
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/master-partner/signup`);
                alert('Link copied!');
              }}
              className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
            >
              <Copy className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl p-4 shadow-sm" data-testid="stat-total">
            <p className="text-sm text-gray-500">Total</p>
            <p className="text-2xl font-bold text-gray-900">{stats.master_partners?.total || 0}</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-4">
            <p className="text-sm text-amber-600">Pending</p>
            <p className="text-2xl font-bold text-amber-700">{stats.master_partners?.pending || 0}</p>
          </div>
          <div className="bg-blue-50 rounded-xl p-4">
            <p className="text-sm text-blue-600">Under Review</p>
            <p className="text-2xl font-bold text-blue-700">{stats.master_partners?.under_review || 0}</p>
          </div>
          <div className="bg-green-50 rounded-xl p-4">
            <p className="text-sm text-green-600">Approved</p>
            <p className="text-2xl font-bold text-green-700">{stats.master_partners?.approved || 0}</p>
          </div>
          <div className="bg-purple-50 rounded-xl p-4">
            <p className="text-sm text-purple-600">Partners Recruited</p>
            <p className="text-2xl font-bold text-purple-700">{stats.total_partners_recruited || 0}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search master partners..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              data-testid="search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              data-testid="status-filter"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="under_review">Under Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="suspended">Suspended</option>
            </select>
            <button
              onClick={fetchData}
              className="p-2.5 rounded-lg hover:bg-gray-100"
              title="Refresh"
            >
              <RefreshCw className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      {/* Master Partners Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto" />
          </div>
        ) : filteredPartners.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <Building2 className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-medium">No Master Partners Found</h3>
            <p className="mt-1">
              {searchQuery || statusFilter ? 'Try adjusting your filters' : 'Share the signup link to onboard master partners'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="master-partners-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Master Partner</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Partners</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Commission</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Lead Creation</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredPartners.map((mp) => (
                  <tr key={mp.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{mp.company_name}</p>
                        <p className="text-sm text-gray-500">{mp.contact_person}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <p className="text-gray-900">{mp.email}</p>
                        <p className="text-gray-500">{mp.phone}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(mp.status)}`}>
                        {mp.status?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <p className="font-medium text-gray-900">{mp.total_partners_recruited || 0} total</p>
                        <p className="text-green-600">{mp.total_partners_approved || 0} approved</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-900">{mp.commission_percentage || 10}%</p>
                    </td>
                    <td className="px-6 py-4">
                      {mp.status === 'approved' ? (
                        <button
                          onClick={() => handleToggleLeadCreation(mp)}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                            mp.can_create_leads ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {mp.can_create_leads ? (
                            <><ToggleRight className="w-4 h-4" /> Enabled</>
                          ) : (
                            <><ToggleLeft className="w-4 h-4" /> Disabled</>
                          )}
                        </button>
                      ) : (
                        <span className="text-gray-400 text-sm">N/A</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {mp.status === 'approved' && (
                          <button
                            onClick={() => copyReferralLink(mp.referral_code)}
                            className="p-1.5 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded"
                            title="Copy referral link"
                          >
                            {copied === mp.referral_code ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setSelectedPartner(mp);
                            setShowDetailModal(true);
                          }}
                          className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                          title="View details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {(mp.status === 'pending' || mp.status === 'under_review') && (
                          <>
                            <button
                              onClick={() => {
                                setSelectedPartner(mp);
                                setShowApproveModal(true);
                              }}
                              className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded"
                              title="Approve"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedPartner(mp);
                                setShowRejectModal(true);
                              }}
                              className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                              title="Reject"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {mp.status === 'approved' && (
                          <button
                            onClick={() => {
                              setSelectedPartner(mp);
                              setShowResetPasswordModal(true);
                            }}
                            className="p-1.5 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded"
                            title="Reset password"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => openDeleteModal(mp)}
                          className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Approve Modal */}
      {showApproveModal && selectedPartner && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b">
              <h3 className="text-lg font-bold text-gray-900">Approve Master Partner</h3>
              <p className="text-sm text-gray-500 mt-1">{selectedPartner.company_name}</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Commission Percentage</label>
                <input
                  type="number"
                  value={approvalSettings.commission_percentage}
                  onChange={(e) => setApprovalSettings({ ...approvalSettings, commission_percentage: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  min="0"
                  max="100"
                />
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="canCreateLeads"
                  checked={approvalSettings.can_create_leads}
                  onChange={(e) => setApprovalSettings({ ...approvalSettings, can_create_leads: e.target.checked })}
                  className="w-5 h-5 rounded border-gray-300 text-purple-600"
                />
                <label htmlFor="canCreateLeads" className="text-sm text-gray-700">
                  Allow direct lead creation
                </label>
              </div>
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => setShowApproveModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={actionLoading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              >
                {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedPartner && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b">
              <h3 className="text-lg font-bold text-gray-900">Reject Master Partner</h3>
              <p className="text-sm text-gray-500 mt-1">{selectedPartner.company_name}</p>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Rejection Reason *</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                rows={4}
                placeholder="Please provide a reason for rejection..."
                required
              />
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason('');
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={actionLoading || !rejectReason.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && selectedPartner && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b">
              <h3 className="text-lg font-bold text-gray-900">Reset Password</h3>
              <p className="text-sm text-gray-500 mt-1">{selectedPartner.company_name}</p>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">New Password *</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="Min 6 characters"
                minLength={6}
              />
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowResetPasswordModal(false);
                  setNewPassword('');
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleResetPassword}
                disabled={actionLoading || newPassword.length < 6}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
              >
                {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedPartner && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">Master Partner Details</h3>
              <button
                onClick={() => setShowDetailModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Company Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Company Name:</span>
                    <p className="font-medium">{selectedPartner.company_name}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Business Type:</span>
                    <p className="font-medium">{selectedPartner.business_type || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">GST Number:</span>
                    <p className="font-medium">{selectedPartner.gst_number || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Status:</span>
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedPartner.status)}`}>
                      {selectedPartner.status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Contact Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Contact Person:</span>
                    <p className="font-medium">{selectedPartner.contact_person}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Email:</span>
                    <p className="font-medium">{selectedPartner.email}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Phone:</span>
                    <p className="font-medium">{selectedPartner.phone}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Address:</span>
                    <p className="font-medium">{selectedPartner.address || '-'}, {selectedPartner.city || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Referral Link Section */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Partner Referral Link</h4>
                {selectedPartner.public_link_enabled ? (
                  <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-sm font-medium text-green-700">Link is Active</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        readOnly
                        value={`https://visaasure.com/partner/signup/${selectedPartner.referral_code}`}
                        className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm font-mono"
                      />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`https://visaasure.com/partner/signup/${selectedPartner.referral_code}`);
                          alert('Link copied to clipboard!');
                        }}
                        className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                        title="Copy link"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Referral Code: <span className="font-mono font-medium">{selectedPartner.referral_code}</span>
                    </p>
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-gray-500">
                      <XCircle className="w-5 h-5" />
                      <span className="text-sm">Link is not active</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      The referral link will be enabled once the partner is approved, documents are verified, and agreement is accepted.
                    </p>
                    {selectedPartner.referral_code && (
                      <p className="text-xs text-gray-500 mt-1">
                        Referral Code: <span className="font-mono font-medium">{selectedPartner.referral_code}</span>
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Stats */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Performance</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-purple-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-purple-700">{selectedPartner.total_partners_recruited || 0}</p>
                    <p className="text-sm text-purple-600">Partners Recruited</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-blue-700">{selectedPartner.total_combined_leads || 0}</p>
                    <p className="text-sm text-blue-600">Total Leads</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-green-700">₹{(selectedPartner.total_commission_earned || 0).toLocaleString()}</p>
                    <p className="text-sm text-green-600">Commission Earned</p>
                  </div>
                </div>
              </div>

              {/* Documents */}
              {selectedPartner.documents?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Documents</h4>
                  <div className="space-y-2">
                    {selectedPartner.documents.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="font-medium text-sm">{doc.document_name || doc.document_type}</p>
                            <p className="text-xs text-gray-500">{doc.file_name}</p>
                            {doc.rejection_reason && (
                              <p className="text-xs text-red-500 mt-1">Reason: {doc.rejection_reason}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            doc.status === 'approved' ? 'bg-green-100 text-green-700' :
                            doc.status === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {doc.status}
                          </span>
                          <a
                            href={doc.file_path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-purple-600 hover:text-purple-700"
                          >
                            View
                          </a>
                          {doc.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleApproveDocument(selectedPartner.id, doc.id)}
                                disabled={actionLoading}
                                className="p-1 text-green-600 hover:bg-green-50 rounded"
                                title="Approve"
                              >
                                <FileCheck className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedDocument(doc);
                                  setShowDocRejectModal(true);
                                }}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                                title="Reject"
                              >
                                <FileX className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Agreement Status */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Agreement Status</h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  {selectedPartner.agreement_accepted ? (
                    <div className="flex items-center gap-2 text-green-700">
                      <Check className="w-5 h-5" />
                      <span>Agreement accepted on {new Date(selectedPartner.agreement_accepted_at).toLocaleDateString()}</span>
                      <span className="text-sm text-gray-500">(v{selectedPartner.agreement_version_accepted})</span>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-amber-700">
                        <AlertCircle className="w-5 h-5" />
                        <span>Agreement not yet accepted</span>
                      </div>
                      {selectedPartner.status === 'approved' && (
                        <div>
                          {selectedPartner.documents?.every(d => d.status === 'approved') ? (
                            <button
                              onClick={() => handleSendAgreement(selectedPartner.id)}
                              disabled={actionLoading}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm"
                            >
                              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                              Send Agreement Email
                            </button>
                          ) : (
                            <p className="text-sm text-gray-500">All documents must be approved before sending agreement</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Recruitment Status */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Can Recruit Partners?</h4>
                <div className={`rounded-lg p-4 ${
                  selectedPartner.status === 'approved' && 
                  selectedPartner.documents?.every(d => d.status === 'approved') && 
                  selectedPartner.agreement_accepted
                    ? 'bg-green-50'
                    : 'bg-amber-50'
                }`}>
                  {selectedPartner.status === 'approved' && 
                   selectedPartner.documents?.every(d => d.status === 'approved') && 
                   selectedPartner.agreement_accepted ? (
                    <div className="flex items-center gap-2 text-green-700">
                      <Check className="w-5 h-5" />
                      <span>Yes - Master partner can recruit partners using their referral link</span>
                    </div>
                  ) : (
                    <div className="space-y-2 text-amber-700">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-5 h-5" />
                        <span>No - The following requirements must be met:</span>
                      </div>
                      <ul className="list-disc list-inside text-sm ml-7 space-y-1">
                        {selectedPartner.status !== 'approved' && (
                          <li>Account must be approved</li>
                        )}
                        {!selectedPartner.documents?.every(d => d.status === 'approved') && (
                          <li>All documents must be approved</li>
                        )}
                        {!selectedPartner.agreement_accepted && (
                          <li>Agreement must be accepted</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Reject Modal */}
      {showDocRejectModal && selectedDocument && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b">
              <h3 className="text-lg font-bold text-gray-900">Reject Document</h3>
              <p className="text-sm text-gray-500 mt-1">{selectedDocument.document_name || selectedDocument.document_type}</p>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Rejection Reason *</label>
              <textarea
                value={docRejectReason}
                onChange={(e) => setDocRejectReason(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                rows={4}
                placeholder="Please provide a reason..."
                required
              />
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDocRejectModal(false);
                  setDocRejectReason('');
                  setSelectedDocument(null);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectDocument}
                disabled={actionLoading || !docRejectReason.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Reject Document
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && partnerToDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-center w-16 h-16 mx-auto bg-red-100 rounded-full mb-4">
                <Trash2 className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 text-center mb-2">Delete Master Partner</h3>
              <p className="text-gray-600 text-center mb-2">
                Are you sure you want to delete this master partner?
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="font-semibold text-gray-900">{partnerToDelete.company_name}</p>
                <p className="text-sm text-gray-500">{partnerToDelete.email}</p>
                <p className="text-sm text-gray-500">Contact: {partnerToDelete.contact_person}</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-red-700 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>This action cannot be undone. All data associated with this master partner will be permanently deleted.</span>
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t bg-gray-50 rounded-b-2xl">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setPartnerToDelete(null);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={actionLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2 transition-colors"
              >
                {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterPartnerManager;
